import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { BacklogEntry, ItemBacklog } from "../../../api/types";
import { Button, Card, EmptyState } from "../../../ui";
import { navigatorHref } from "../model/words";

/**
 * The item backlog: items other documents have lines for ("belongs to another item"), with the most lines
 * first. Each has a Start session button (reading Q52). The session opens on the item, with its parked lines
 * offered.
 */
export function Backlog({ backlog, canStart }: { backlog: ItemBacklog; canStart: boolean }) {
  const navigate = useNavigate();
  const start = useApiCommand("start_session_from_backlog");
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<{ key: string; text: string } | null>(null);
  const items = [...backlog.items].sort((a, b) => b.count - a.count);

  const begin = async (entry: BacklogEntry) => {
    setBusy(entry.item_name_norm);
    setProblem(null);
    try {
      const reply = await start.mutateAsync({ item_name_norm: entry.item_name_norm });
      void navigate(`/sessions/${encodeURIComponent(reply.data.session.id)}`);
    } catch (error) {
      setProblem({ key: entry.item_name_norm, text: refusalText(error) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card aria-labelledby="backlog-title" className="nv-backlog">
      <h2 id="backlog-title">Items waiting for a session</h2>
      {items.length === 0 ? (
        <EmptyState title="No lines are parked for another item">
          When a document has lines about an item you are not working on, they wait here until you start a session for that item.
        </EmptyState>
      ) : (
        <ul className="nv-backlog__list">
          {items.map((entry) => (
            <li key={entry.item_name_norm} className="nv-backlog__item">
              <div className="nv-backlog__text">
                <strong>{entry.element_id ? <Link to={navigatorHref(entry.element_id)}>{entry.item_name}</Link> : entry.item_name}</strong>
                <span className="muted">
                  {" "}
                  {entry.count === 1 ? "1 parked line" : `${entry.count} parked lines`}
                  {entry.deliverables.length > 0 && ` from ${entry.deliverables.join(", ")}`}
                  {!entry.element_id && ". Not in the tree yet."}
                </span>
                {problem?.key === entry.item_name_norm && (
                  <p role="alert" className="field__error">
                    {problem.text}
                  </p>
                )}
              </div>
              {canStart && (
                <Button variant="secondary" busy={busy === entry.item_name_norm} onClick={() => void begin(entry)} aria-label={`Start a session on ${entry.item_name}`}>
                  Start session
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
